import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../master/SideBar";
import Nav from "../master/NavBar";
import { SweetAlertShow, LoadingDiv } from "../master/Master";
import {
  studentManualFeeDeposit,
  studentFeeReceiptNo,
  studentsListOnly,
  studentAdd,
  studentRegistrationNo,
} from "../../api/Student";
import { BankList } from "../../api/BankService";
import Master from "../studentFeeReport/Master";
import { formatToDate } from "../master/FormatToDateTime";

export const feeTypes = [
  "Registration Fee",
  "Form Fee",
  "Examination Fee",
  "Computer Fee",
  "Sports Fee",
  "Stationery Fee",
  "Late Fee",
  "Development Fee",
  "Id Card Fee",
  "Books Fee",
  "Uniform Fee",
  "Kit Fee",
  "Caution Money",
  "Other Charges",
  "Previous Session Fees",
];

const getReceiptPrefix = (receiptNo) => {
  if (!receiptNo) return "";

  const match = String(receiptNo).match(/^[A-Za-z]+[-_]?/);

  return match?.[0] || "";
};

const getReceiptNumber = (receiptNo) => {
  if (!receiptNo) return "";

  return String(receiptNo).replace(/^[A-Za-z]+[-_]?/, "");
};

export default function StudentManualFeeDeposit() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [students, setStudents] = useState([]);
  const [banks, setBanks] = useState([]);
  const [receiptNoData, setReceiptNoData] = useState({});
  const [setting, setSetting] = useState({});

  const [studentSearch, setStudentSearch] = useState("");
  const [studentDropdownOpen, setStudentDropdownOpen] = useState(false);
  const [studentModalOpen, setStudentModalOpen] = useState(false);
  const [studentSaving, setStudentSaving] = useState(false);

  const [receiptPrefix, setReceiptPrefix] = useState("");

  const [newStudent, setNewStudent] = useState({
    registration_no: "",
    name: "",
    father_name: "",
    phone: "",
    gender: "",
  });

  const [formData, setFormData] = useState({
    student_id: "",
    receipt_no: "",
    fee_details: [
      {
        fee_type: "",
        amount: "",
      },
    ],
    amount: "",
    bank_id: "",
    transaction_no: "",
    remark: "",
    payment_date: "",
  });

  const selectedStudent = useMemo(() => {
    return students?.find(
      (student) => String(student?.id) === String(formData?.student_id),
    );
  }, [students, formData?.student_id]);

  const addFeeRow = () => {
    setFormData((prev) => ({
      ...prev,
      fee_details: [
        ...prev.fee_details,
        {
          fee_type: "",
          amount: "",
        },
      ],
    }));
  };

  const removeFeeRow = (index) => {
    setFormData((prev) => ({
      ...prev,
      fee_details: prev.fee_details.filter((_, i) => i !== index),
    }));
  };

  const handleFeeDetailChange = (index, field, value) => {
    setFormData((prev) => ({
      ...prev,
      fee_details: prev.fee_details.map((item, i) =>
        i === index
          ? {
              ...item,
              [field]: value,
            }
          : item,
      ),
    }));
  };

  const totalAmount = formData.fee_details.reduce(
    (total, item) => total + Number(item.amount || 0),
    0,
  );

  const filteredStudents = useMemo(() => {
    const search = studentSearch?.trim()?.toLowerCase();

    if (!search) {
      return students;
    }

    return students.filter((student) => {
      const name = String(student?.name ?? "").toLowerCase();
      const registrationNo = String(
        student?.registration_no ?? "",
      ).toLowerCase();
      const phone = String(student?.phone ?? "").toLowerCase();
      const fatherName = String(student?.father_name ?? "").toLowerCase();

      return (
        name.includes(search) ||
        registrationNo.includes(search) ||
        phone.includes(search) ||
        fatherName.includes(search)
      );
    });
  }, [students, studentSearch]);

  useEffect(() => {
    try {
      const storedSetting = localStorage.getItem("setting");
      if (storedSetting) {
        setSetting(JSON.parse(storedSetting));
      }
    } catch (error) {
      setSetting({});
    }
  }, []);

  useEffect(() => {
    const today = new Date();
    setFormData((prev) => ({
      ...prev,
      payment_date: formatDate(today),
    }));
  }, []);

  useEffect(() => {
    getStudentList();
    getBankList();
  }, []);

  const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  };

  const getStudentList = async () => {
    setLoading(true);

    try {
      const response = await studentsListOnly("limit=all");
      if (response?.data?.status) {
        setStudents(response?.data?.data || []);
      } else {
        setStudents([]);
        SweetAlertShow(
          "info",
          "Warning",
          response?.data?.message || "Unable to load students.",
        );
      }
    } catch (error) {
      setStudents([]);
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ??
          error?.response?.message ??
          error?.message ??
          "Unable to load students.",
      );
    } finally {
      setLoading(false);
    }
  };

  const getBankList = async () => {
    setLoading(true);
    try {
      const response = await BankList();

      if (response?.data?.status) {
        setBanks(response?.data?.data || []);
      } else {
        setBanks([]);

        SweetAlertShow(
          "info",
          "Warning",
          response?.data?.message || "Unable to load banks.",
        );
      }
    } catch (error) {
      setBanks([]);

      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ??
          error?.response?.message ??
          error?.message ??
          "Unable to load banks.",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    initializeReceiptNumber();
  }, []);

  useEffect(() => {
    if (!formData?.receipt_no) {
      return;
    }
    receiptNoVerify();
  }, [formData?.receipt_no]);

  const initializeReceiptNumber = async () => {
    try {
      const response = await studentFeeReceiptNo("");

      if (response?.data?.status) {
        const nextReceiptNo = response?.data?.data?.next_receipt_no || "";

        if (nextReceiptNo) {
          applyReceiptNumber(nextReceiptNo);
        }

        setReceiptNoData(response?.data?.data || {});
      } else {
        setReceiptNoData(response?.data?.data || {});

        SweetAlertShow(
          "info",
          "Warning",
          response?.data?.message || "Unable to generate receipt number.",
        );
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ??
          error?.message ??
          "Unable to generate receipt number.",
      );
    }
  };

  const applyReceiptNumber = (receiptNo) => {
    const receipt = String(receiptNo || "").trim();

    const prefix = getReceiptPrefix(receipt);

    setReceiptPrefix(prefix);

    setFormData((prev) => ({
      ...prev,
      receipt_no: receipt,
    }));
  };

  const receiptNoVerify = async () => {
    try {
      const response = await studentFeeReceiptNo(
        `receipt_no=${encodeURIComponent(formData?.receipt_no)}`,
      );

      if (response?.data?.status) {
        setReceiptNoData(response?.data?.data || {});
      } else {
        setReceiptNoData(response?.data?.data || {});

        SweetAlertShow(
          "info",
          "Warning",
          response?.data?.message || "Invalid receipt number.",
        );
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ??
          error?.message ??
          "Unable to verify receipt number.",
      );
    }
  };

  const handleReceiptNumberChange = (e) => {
    const numberOnly = e.target.value.replace(/\D/g, "");

    setFormData((prev) => ({
      ...prev,
      receipt_no: `${receiptPrefix}${numberOnly}`,
    }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleStudentSelect = (student) => {
    setFormData((prev) => ({
      ...prev,
      student_id: student?.id || "",
    }));
    setStudentSearch(
      `${student?.name || ""} (${student?.registration_no || "-"})`,
    );
    setStudentDropdownOpen(false);
  };

  const clearStudent = () => {
    setFormData((prev) => ({
      ...prev,
      student_id: "",
    }));

    setStudentSearch("");
    setStudentDropdownOpen(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData?.student_id) {
      SweetAlertShow("info", "Warning", "Please select a student.");
      return;
    }

    if (!formData?.receipt_no) {
      SweetAlertShow("info", "Warning", "Please enter receipt number.");
      return;
    }

    if (!formData.fee_details?.length) {
      SweetAlertShow("info", "Warning", "Please add at least one fee.");
      return;
    }

    const invalidFee = formData.fee_details.some(
      (item) => !item.fee_type || !item.amount || Number(item.amount) <= 0,
    );

    if (invalidFee) {
      SweetAlertShow(
        "info",
        "Warning",
        "Please select fee type and enter valid amount for all fees.",
      );
      return;
    }

    if (!formData?.bank_id) {
      SweetAlertShow("info", "Warning", "Please select bank.");
      return;
    }

    setSaving(true);

    try {
      const payload = new FormData();
      payload.append("student_id", formData?.student_id);
      payload.append("receipt_no", formData?.receipt_no);
      payload.append("fee_details", JSON.stringify(formData.fee_details));
      payload.append("amount", totalAmount);
      payload.append("amount", totalAmount);
      payload.append("bank_id", formData?.bank_id);
      payload.append("transaction_no", formData?.transaction_no || "");
      payload.append("remark", formData?.remark || "");
      payload.append("payment_date", formData?.payment_date || "");
      payload.append("next_payment_date", formData?.next_payment_date || "");
      if (setting?.message_status === "Yes") {
        const sendMessageElement = document.getElementById("sendMessage");
        if (sendMessageElement?.checked) {
          payload.append("sendMessage", "1");
        }
      }

      const response = await studentManualFeeDeposit(payload);
      if (response?.data?.status) {
        SweetAlertShow(
          "success",
          "Successfully",
          response?.data?.message || "Fee deposited successfully.",
        );
        navigate(`/student/fees/receipt?id=${response?.data?.data?.id}`);
      }
      SweetAlertShow(
        "info",
        "Warning",
        Array.isArray(response?.data?.message)
          ? response.data.message.join(", ")
          : response?.data?.message || "Unable to deposit fee.",
      );
    } catch (error) {
      const message =
        error?.response?.data?.message ??
        error?.response?.message ??
        error?.message ??
        "Something went wrong.";

      SweetAlertShow(
        "error",
        "Error",
        Array.isArray(message) ? message.join(", ") : message,
      );
    } finally {
      setSaving(false);
    }
  };

  const FieldSkeleton = ({ height = 56 }) => (
    <div className="placeholder-glow w-100">
      <span
        className="placeholder w-100 rounded-3"
        style={{
          height,
          display: "block",
        }}
      />
    </div>
  );

  const getNewStudentRegistrationNo = async () => {
    try {
      const response = await studentRegistrationNo("");

      if (response?.data?.status) {
        setNewStudent((prev) => ({
          ...prev,
          registration_no: response?.data?.data?.next_registration_no || "",
        }));
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ??
          error?.message ??
          "Unable to generate registration number.",
      );
    }
  };

  const openStudentModal = () => {
    setStudentModalOpen(true);

    setNewStudent({
      registration_no: "",
      name: "",
      father_name: "",
      phone: "",
      gender: "",
    });

    getNewStudentRegistrationNo();
  };

  const closeStudentModal = () => {
    if (studentSaving) return;

    setStudentModalOpen(false);
  };

  const handleQuickStudentAdd = async (e) => {
    e.preventDefault();
    if (!newStudent.registration_no) {
      SweetAlertShow("info", "Warning", "Please enter registration number.");
      return;
    }
    if (!newStudent.name?.trim()) {
      SweetAlertShow("info", "Warning", "Please enter student name.");
      return;
    }
    if (!newStudent.father_name?.trim()) {
      SweetAlertShow("info", "Warning", "Please enter father name.");
      return;
    }
    if (!newStudent.phone?.trim()) {
      SweetAlertShow("info", "Warning", "Please enter phone number.");
      return;
    }
    if (!newStudent.gender) {
      SweetAlertShow("info", "Warning", "Please select gender.");
      return;
    }
    setStudentSaving(true);

    try {
      const payload = new FormData();
      payload.append("registration_no", newStudent.registration_no);
      payload.append("name", newStudent.name.trim());
      payload.append("father_name", newStudent.father_name.trim());
      payload.append("phone", newStudent.phone.trim());
      payload.append("gender", newStudent.gender);
      payload.append("admission_date", formatToDate(new Date()));
      const response = await studentAdd(payload);
      if (response?.data?.status) {
        const createdStudent = response?.data?.data;

        SweetAlertShow(
          "success",
          "Successfully",
          response?.data?.message || "Student added successfully.",
        );
        if (createdStudent?.id) {
          setStudents((prev) => [createdStudent, ...(prev || [])]);

          setFormData((prev) => ({
            ...prev,
            student_id: createdStudent.id,
          }));

          setStudentSearch(
            `${createdStudent?.name || newStudent.name} (${
              createdStudent?.registration_no || newStudent.registration_no
            })`,
          );
        }

        setStudentModalOpen(false);
      } else {
        SweetAlertShow(
          "info",
          "Warning",
          Array.isArray(response?.data?.message)
            ? response.data.message.join(", ")
            : response?.data?.message || "Unable to add student.",
        );
      }
    } catch (error) {
      const message =
        error?.response?.data?.message ??
        error?.response?.message ??
        error?.message ??
        "Something went wrong.";
      SweetAlertShow(
        "error",
        "Error",
        Array.isArray(message) ? message.join(", ") : message,
      );
    } finally {
      setStudentSaving(false);
    }
  };

  return (
    <>
      {(loading || saving) && <LoadingDiv />}

      <div className="layout-wrapper layout-content-navbar">
        <div className="layout-container">
          <Header />

          <div className="layout-page">
            <Nav />

            <div className="content-wrapper">
              <div className="container-fluid flex-grow-1 container-p-y">
                <div className="row">
                  <Master />
                  <div className="col-12">
                    <div className="card border-0 shadow-sm mb-3">
                      <div className="card-body p-4">
                        <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
                          <div className="d-flex align-items-center gap-3">
                            <div
                              className="d-flex align-items-center justify-content-center rounded-3 bg-primary text-white"
                              style={{
                                width: 50,
                                height: 50,
                                flexShrink: 0,
                              }}
                            >
                              <i className="ri-wallet-3-line fs-4" />
                            </div>

                            <div>
                              <h5 className="mb-1 fw-bold">
                                Manual Fee Deposit
                              </h5>

                              <small className="text-muted">
                                Record student fee payment manually
                              </small>
                            </div>
                          </div>

                          <div className="d-flex align-items-center gap-2">
                            <span className="badge bg-label-primary px-3 py-2">
                              <i className="ri-file-list-3-line me-1" />
                              Manual Payment
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {selectedStudent && (
                      <div className="my-3">
                        <div className="rounded-3 bg-label-primary p-3">
                          <div className="d-flex align-items-center gap-3">
                            <div
                              className="rounded-circle bg-primary text-white d-flex align-items-center justify-content-center"
                              style={{
                                width: 48,
                                height: 48,
                                flexShrink: 0,
                              }}
                            >
                              <i className="ri-user-3-fill fs-5" />
                            </div>

                            <div className="flex-grow-1">
                              <div className="fw-bold">
                                {selectedStudent?.name || "-"}
                              </div>

                              <div className="small text-muted mt-1">
                                <span className="me-3">
                                  <i className="ri-id-card-line me-1" />
                                  {selectedStudent?.registration_no || "-"}
                                </span>

                                <span className="me-3">
                                  <i className="ri-phone-line me-1" />
                                  {selectedStudent?.phone || "-"}
                                </span>

                                {selectedStudent?.father_name && (
                                  <span>
                                    <i className="ri-user-line me-1" />
                                    {selectedStudent.father_name}
                                  </span>
                                )}
                              </div>
                            </div>

                            <span className="badge bg-success">
                              <i className="ri-checkbox-circle-line me-1" />
                              Selected
                            </span>
                          </div>
                        </div>
                      </div>
                    )}

                    <form onSubmit={handleSubmit}>
                      <div className="card border-0 shadow-sm mb-3">
                        <div className="card-header bg-transparent border-0 p-4 pb-2">
                          <h6 className="fw-bold mb-0">
                            <i className="ri-user-search-line text-primary me-2" />
                            Student & Receipt
                          </h6>
                        </div>

                        <div className="card-body p-4 pt-3">
                          <div className="row g-3">
                            {/* Student */}

                            <div className="col-md-6">
                              {loading && students.length === 0 ? (
                                <FieldSkeleton />
                              ) : (
                                <div
                                  className="position-relative"
                                  style={{
                                    zIndex: studentDropdownOpen ? 20 : 1,
                                  }}
                                >
                                  <div className="form-floating form-floating-outline">
                                    <input
                                      type="text"
                                      className="form-control"
                                      placeholder="Search Student"
                                      value={studentSearch}
                                      autoComplete="off"
                                      onFocus={() =>
                                        setStudentDropdownOpen(true)
                                      }
                                      onChange={(e) => {
                                        setStudentSearch(e.target.value);
                                        setStudentDropdownOpen(true);

                                        if (formData.student_id) {
                                          setFormData((prev) => ({
                                            ...prev,
                                            student_id: "",
                                          }));
                                        }
                                      }}
                                    />

                                    <label>
                                      <i className="ri-user-search-line me-1" />
                                      Search Student
                                      <sup className="text-danger">*</sup>
                                    </label>

                                    {studentSearch && (
                                      <button
                                        type="button"
                                        className="btn btn-sm position-absolute top-50 end-0 translate-middle-y me-2"
                                        style={{
                                          zIndex: 3,
                                        }}
                                        onClick={clearStudent}
                                      >
                                        <i className="ri-close-line" />
                                      </button>
                                    )}
                                  </div>

                                  {/* Search Dropdown */}

                                  {studentDropdownOpen && (
                                    <div
                                      className="position-absolute bg-white border rounded-3 shadow-lg mt-1 w-100"
                                      style={{
                                        maxHeight: 320,
                                        overflowY: "auto",
                                      }}
                                    >
                                      {filteredStudents.length > 0 ? (
                                        filteredStudents.map((student) => (
                                          <button
                                            type="button"
                                            key={student.id}
                                            className={`dropdown-item text-start p-3 border-bottom ${
                                              String(student.id) ===
                                              String(formData.student_id)
                                                ? "bg-label-primary"
                                                : ""
                                            }`}
                                            onClick={() =>
                                              handleStudentSelect(student)
                                            }
                                          >
                                            <div className="d-flex align-items-center gap-3">
                                              <div
                                                className="rounded-circle bg-label-primary d-flex align-items-center justify-content-center"
                                                style={{
                                                  width: 40,
                                                  height: 40,
                                                  flexShrink: 0,
                                                }}
                                              >
                                                <i className="ri-user-3-line" />
                                              </div>

                                              <div className="flex-grow-1">
                                                <div className="fw-semibold">
                                                  {student?.name || "-"}
                                                </div>

                                                <small className="text-muted">
                                                  {student?.registration_no ||
                                                    "-"}
                                                  {" • "}
                                                  {student?.phone || "-"}
                                                </small>
                                              </div>

                                              <i className="ri-arrow-right-s-line text-muted" />
                                            </div>
                                          </button>
                                        ))
                                      ) : (
                                        <div className="mt-2 p-3 rounded-3 bg-label-warning d-flex align-items-center justify-content-between gap-3">
                                          <div>
                                            <div className="fw-semibold">
                                              <i className="ri-error-warning-line me-1" />
                                              No students found
                                            </div>

                                            <small className="text-muted">
                                              Please add a student first.
                                            </small>
                                          </div>

                                          <button
                                            type="button"
                                            className="btn btn-sm btn-primary d-flex align-items-center gap-1"
                                            onClick={openStudentModal}
                                          >
                                            <i className="ri-user-add-line" />
                                            Add Student
                                          </button>
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>
                              )}

                              {students.length === 0 && !loading && (
                                <div className="mt-2 p-3 rounded-3 bg-label-warning d-flex align-items-center justify-content-between gap-3">
                                  <div>
                                    <div className="fw-semibold">
                                      <i className="ri-error-warning-line me-1" />
                                      No students found
                                    </div>

                                    <small className="text-muted">
                                      Please add a student first.
                                    </small>
                                  </div>

                                  <button
                                    type="button"
                                    className="btn btn-sm btn-primary d-flex align-items-center gap-1"
                                    onClick={openStudentModal}
                                  >
                                    <i className="ri-user-add-line" />
                                    Add Student
                                  </button>
                                </div>
                              )}
                            </div>

                            {/* Receipt */}
                            <div className="col-md-6">
                              {loading ? (
                                <FieldSkeleton />
                              ) : (
                                <>
                                  <div className="form-floating form-floating-outline">
                                    <div className="d-flex align-items-stretch">
                                      {receiptPrefix && (
                                        <div
                                          className="d-flex align-items-center px-3 border border-end-0 rounded-start"
                                          style={{
                                            backgroundColor:
                                              "var(--bs-gray-100)",
                                            fontWeight: 600,
                                            color: "var(--bs-primary)",
                                            fontSize: "16px",
                                            whiteSpace: "nowrap",
                                          }}
                                        >
                                          <i className="ri-receipt-line me-2" />
                                          {receiptPrefix}
                                        </div>
                                      )}

                                      <input
                                        type="text"
                                        id="receipt_no_number"
                                        className={`form-control ${
                                          receiptNoData?.message
                                            ? "is-invalid"
                                            : ""
                                        }`}
                                        placeholder="Receipt Number"
                                        value={getReceiptNumber(
                                          formData?.receipt_no,
                                        )}
                                        inputMode="numeric"
                                        onChange={handleReceiptNumberChange}
                                        required
                                        style={
                                          receiptPrefix
                                            ? {
                                                borderTopLeftRadius: 0,
                                                borderBottomLeftRadius: 0,
                                              }
                                            : undefined
                                        }
                                      />
                                    </div>
                                  </div>

                                  {receiptNoData?.message && (
                                    <small className="text-danger mt-1 d-block">
                                      <i className="ri-error-warning-line me-1" />
                                      {receiptNoData.message}
                                    </small>
                                  )}
                                </>
                              )}
                            </div>
                          </div>

                          {/* Selected Student */}

                          <hr />

                          <div className="d-flex justify-content-between align-items-center mb-3">
                            <h6 className="fw-bold mb-0">
                              <i className="ri-money-rupee-circle-line text-success me-2" />
                              Fee Details
                            </h6>

                            <button
                              type="button"
                              className="btn btn-sm btn-primary"
                              onClick={addFeeRow}
                            >
                              <i className="ri-add-line me-1" />
                              Add Fee
                            </button>
                          </div>

                          {formData.fee_details.map((item, index) => (
                            <div className="row g-3 mb-3" key={index}>
                              {/* Fee Type */}
                              <div className="col-md-6">
                                <div className="form-floating form-floating-outline">
                                  <select
                                    className="form-select"
                                    value={item.fee_type}
                                    onChange={(e) =>
                                      handleFeeDetailChange(
                                        index,
                                        "fee_type",
                                        e.target.value,
                                      )
                                    }
                                    required
                                  >
                                    <option value="">Select Fee Type</option>

                                    {feeTypes.map((feeType) => (
                                      <option
                                        key={feeType}
                                        value={feeType}
                                        disabled={formData.fee_details.some(
                                          (fee, i) =>
                                            i !== index &&
                                            fee.fee_type === feeType,
                                        )}
                                      >
                                        {feeType}
                                      </option>
                                    ))}
                                  </select>

                                  <label>
                                    <i className="ri-price-tag-3-line me-1" />
                                    Fee Type
                                    <sup className="text-danger">*</sup>
                                  </label>
                                </div>
                              </div>

                              {/* Amount */}
                              <div className="col-md-5">
                                <div className="form-floating form-floating-outline">
                                  <input
                                    type="number"
                                    className="form-control fw-semibold"
                                    placeholder="Enter Amount"
                                    min="0"
                                    step="0.01"
                                    value={item.amount}
                                    onChange={(e) =>
                                      handleFeeDetailChange(
                                        index,
                                        "amount",
                                        e.target.value,
                                      )
                                    }
                                    required
                                  />

                                  <label>
                                    <i className="ri-money-rupee-circle-line me-1" />
                                    Amount
                                    <sup className="text-danger">*</sup>
                                  </label>
                                </div>
                              </div>

                              {/* Remove */}
                              <div className="col-md-1 d-flex align-items-center">
                                {formData.fee_details.length > 1 && (
                                  <button
                                    type="button"
                                    className="btn btn-outline-danger"
                                    onClick={() => removeFeeRow(index)}
                                    title="Remove"
                                  >
                                    <i className="ri-delete-bin-line" />
                                  </button>
                                )}
                              </div>
                            </div>
                          ))}

                          {/* Total */}
                          <div className="d-flex justify-content-end mt-3">
                            <div className="bg-label-success rounded-3 px-4 py-3">
                              <span className="text-muted me-3">
                                Total Amount
                              </span>

                              <strong className="fs-5 text-success">
                                ₹ {totalAmount.toFixed(2)}
                              </strong>
                            </div>
                          </div>

                          <h6 className="fw-bold mt-2">
                            <i className="ri-bank-card-line text-primary me-2" />
                            Payment Details
                          </h6>
                          <div className="row g-3">
                            {/* Bank */}

                            <div className="col-md-6">
                              {loading && banks.length === 0 ? (
                                <FieldSkeleton />
                              ) : (
                                <div className="form-floating form-floating-outline">
                                  <select
                                    name="bank_id"
                                    id="bank_id"
                                    className="form-select"
                                    value={formData?.bank_id || ""}
                                    onChange={handleChange}
                                    required
                                  >
                                    <option value="">Select Bank</option>

                                    {banks?.map((item) => (
                                      <option key={item?.id} value={item?.id}>
                                        {item?.account_no}
                                        {" : "}
                                        {item?.owner_name}
                                      </option>
                                    ))}
                                  </select>

                                  <label htmlFor="bank_id">
                                    <i className="ri-bank-line me-1" />
                                    Select Bank
                                    <sup className="text-danger">*</sup>
                                  </label>
                                </div>
                              )}

                              {banks.length === 0 && !loading && (
                                <small className="text-danger mt-1 d-block">
                                  <i className="ri-error-warning-line me-1" />
                                  No banks found.
                                </small>
                              )}
                            </div>

                            {/* Transaction */}

                            <div className="col-md-6">
                              <div className="form-floating form-floating-outline">
                                <input
                                  type="text"
                                  name="transaction_no"
                                  id="transaction_no"
                                  className="form-control"
                                  placeholder="Transaction No"
                                  value={formData?.transaction_no}
                                  onChange={handleChange}
                                />

                                <label htmlFor="transaction_no">
                                  <i className="ri-exchange-line me-1" />
                                  Transaction No
                                </label>
                              </div>
                            </div>
                          </div>

                          <hr />
                          <h6 className="fw-bold">
                            <i className="ri-calendar-check-line text-warning me-2" />
                            Payment Schedule & Remark
                          </h6>
                          <div className="row g-3">
                            {/* Payment Date */}

                            <div className="col-md-6">
                              <div className="form-floating form-floating-outline">
                                <input
                                  type="date"
                                  name="payment_date"
                                  id="payment_date"
                                  className="form-control"
                                  value={formData?.payment_date}
                                  onChange={handleChange}
                                />

                                <label htmlFor="payment_date">
                                  <i className="ri-calendar-event-line me-1" />
                                  Payment Date
                                </label>
                              </div>
                            </div>

                            <div className="col-md-6">
                              <div className="form-floating form-floating-outline">
                                <textarea
                                  name="remark"
                                  id="remark"
                                  className="form-control"
                                  placeholder="Enter Remark"
                                  value={formData?.remark}
                                  onChange={handleChange}
                                />

                                <label htmlFor="remark">
                                  <i className="ri-sticky-note-line me-1" />
                                  Remark
                                </label>
                              </div>
                            </div>
                          </div>
                          <hr />
                          <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
                            {/* Send Message */}

                            <div>
                              {setting?.message_status === "Yes" && (
                                <div className="form-check">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                    id="sendMessage"
                                    name="sendMessage"
                                    defaultChecked
                                    value="1"
                                  />

                                  <label
                                    className="form-check-label"
                                    htmlFor="sendMessage"
                                  >
                                    <i className="ri-whatsapp-line me-1 text-success" />
                                    Send Message
                                  </label>
                                </div>
                              )}
                            </div>

                            {/* Buttons */}

                            <div className="d-flex gap-2">
                              <button
                                type="submit"
                                className="btn btn-primary px-4"
                                disabled={
                                  saving ||
                                  !formData?.student_id ||
                                  !formData?.fee_details?.length ||
                                  totalAmount <= 0 ||
                                  formData.fee_details.some(
                                    (item) =>
                                      !item.fee_type ||
                                      !item.amount ||
                                      Number(item.amount) <= 0,
                                  )
                                }
                              >
                                {saving ? (
                                  <>
                                    <span
                                      className="spinner-border spinner-border-sm me-2"
                                      role="status"
                                    />
                                    Saving...
                                  </>
                                ) : (
                                  <>
                                    <i className="ri-save-3-line me-1" />
                                    Save Fee Deposit
                                  </>
                                )}
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {studentModalOpen && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          style={{
            backgroundColor: "rgba(0, 0, 0, 0.1)",
            zIndex: 1055,
          }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content border-0 shadow-lg rounded-4">
              {/* Header */}
              <div className="modal-header border-0 p-4 pb-2">
                <div className="d-flex align-items-center gap-3">
                  <div
                    className="d-flex align-items-center justify-content-center rounded-3 bg-primary text-white"
                    style={{
                      width: 45,
                      height: 45,
                    }}
                  >
                    <i className="ri-user-add-line fs-5" />
                  </div>

                  <div>
                    <h5 className="modal-title fw-bold mb-0">Add Student</h5>

                    <small className="text-muted">
                      Add a new student quickly
                    </small>
                  </div>
                </div>

                <button
                  type="button"
                  className="btn-close"
                  onClick={closeStudentModal}
                  disabled={studentSaving}
                />
              </div>

              {/* Body */}
              <form onSubmit={handleQuickStudentAdd}>
                <div className="modal-body p-4">
                  <div className="row g-3">
                    {/* Registration No */}
                    <div className="col-md-6">
                      <div className="form-floating form-floating-outline">
                        <input
                          type="text"
                          className="form-control"
                          name="registration_no"
                          placeholder="Registration No"
                          value={newStudent.registration_no}
                          onChange={(e) =>
                            setNewStudent((prev) => ({
                              ...prev,
                              registration_no: e.target.value,
                            }))
                          }
                          required
                        />

                        <label>
                          <i className="ri-id-card-line me-1" />
                          Registration No
                        </label>
                      </div>
                    </div>

                    {/* Student Name */}
                    <div className="col-md-6">
                      <div className="form-floating form-floating-outline">
                        <input
                          type="text"
                          className="form-control"
                          name="name"
                          placeholder="Student Name"
                          value={newStudent.name}
                          onChange={(e) =>
                            setNewStudent((prev) => ({
                              ...prev,
                              name: e.target.value,
                            }))
                          }
                          required
                        />

                        <label>
                          <i className="ri-user-3-line me-1" />
                          Student Name
                        </label>
                      </div>
                    </div>

                    {/* Father Name */}
                    <div className="col-md-6">
                      <div className="form-floating form-floating-outline">
                        <input
                          type="text"
                          className="form-control"
                          name="father_name"
                          placeholder="Father Name"
                          value={newStudent.father_name}
                          onChange={(e) =>
                            setNewStudent((prev) => ({
                              ...prev,
                              father_name: e.target.value,
                            }))
                          }
                          required
                        />

                        <label>
                          <i className="ri-user-line me-1" />
                          Father Name
                        </label>
                      </div>
                    </div>

                    {/* Phone */}
                    <div className="col-md-6">
                      <div className="form-floating form-floating-outline">
                        <input
                          type="tel"
                          className="form-control"
                          name="phone"
                          placeholder="Phone"
                          maxLength={10}
                          value={newStudent.phone}
                          onChange={(e) =>
                            setNewStudent((prev) => ({
                              ...prev,
                              phone: e.target.value.replace(/\D/g, ""),
                            }))
                          }
                          required
                        />

                        <label>
                          <i className="ri-phone-line me-1" />
                          Phone
                        </label>
                      </div>
                    </div>

                    {/* Gender */}
                    <div className="col-12">
                      <div className="form-floating form-floating-outline">
                        <select
                          className="form-select"
                          name="gender"
                          value={newStudent.gender}
                          onChange={(e) =>
                            setNewStudent((prev) => ({
                              ...prev,
                              gender: e.target.value,
                            }))
                          }
                          required
                        >
                          <option value="">Select Gender</option>

                          <option value="Male">Male</option>

                          <option value="Female">Female</option>

                          <option value="Other">Other</option>
                        </select>

                        <label>
                          <i className="ri-men-line me-1" />
                          Gender
                        </label>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Footer */}
                <div className="modal-footer border-0 p-4 pt-0">
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={closeStudentModal}
                    disabled={studentSaving}
                  >
                    Cancel
                  </button>

                  <button
                    type="submit"
                    className="btn btn-primary px-4 d-flex align-items-center gap-2"
                    disabled={studentSaving}
                  >
                    {studentSaving ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm"
                          role="status"
                        />
                        Saving...
                      </>
                    ) : (
                      <>
                        <i className="ri-user-add-line" />
                        Add Student
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
